import { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function RatingDialog({ open, onOpenChange, taskId, laborerId, onDone }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!rating) return toast.error("Please pick a star rating");
    setSaving(true);
    try {
      await api.post("/reviews", { task_id: taskId, laborer_id: laborerId, rating, comment, anonymous });
      toast.success("Review submitted");
      onOpenChange(false);
      onDone?.();
      setRating(0); setComment(""); setAnonymous(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="rating-dialog">
        <DialogHeader>
          <DialogTitle>Rate this helper</DialogTitle>
          <DialogDescription>Your feedback helps others hire with confidence.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-center gap-2 py-3">
          {[1,2,3,4,5].map((n) => (
            <button key={n} onClick={() => setRating(n)} data-testid={`star-${n}`} className="p-1 transition-transform hover:scale-110">
              <Star size={32} className={n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
            </button>
          ))}
        </div>
        <Textarea placeholder="Optional: leave a comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={3} data-testid="rating-comment-input" />
        <div className="flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200 p-3">
          <div>
            <Label htmlFor="anon-switch" className="font-semibold">Submit anonymously</Label>
            <p className="text-xs text-slate-500 mt-0.5">Your name won't appear on the review.</p>
          </div>
          <Switch id="anon-switch" checked={anonymous} onCheckedChange={setAnonymous} data-testid="rating-anon-switch" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="submit-rating-btn" className="bg-[#D9FF3D] hover:bg-[#C6EB2D] text-[#23272B] font-bold">
            {saving ? "Submitting…" : "Submit review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
